<template>
  <div
    :class="['email-component', 'game-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="game-wrapper" :style="wrapperStyles">
      <!-- Layout: Image Left/Right -->
      <div
        v-if="componentData.showImage && isImageSideLayout"
        class="game-image-container"
        :style="imageContainerStyles"
      >
        <div class="placeholder-image" :style="imagePlaceholderStyles">
          <i class="tim-icons icon-controller"></i>
          <span>Event image</span>
        </div>
      </div>

      <!-- Game Content -->
      <div class="game-content" :style="contentStyles">
        <!-- Layout: Image Top -->
        <div
          v-if="componentData.showImage && !isImageSideLayout"
          class="game-image-container"
          :style="imageContainerStyles"
        >
          <div class="placeholder-image" :style="imagePlaceholderStyles">
            <i class="tim-icons icon-controller"></i>
            <span>Event image</span>
          </div>
        </div>

        <!-- Event name (placeholder) -->
        <h3 class="game-name" :style="nameStyles">
          Event name
        </h3>

        <!-- Game Description (placeholder if enabled) -->
        <div
          v-if="componentData.showDescription"
          class="game-description"
          :style="descriptionStyles"
        >
          This is a sample event description that will be replaced with actual content.
        </div>

        <!-- Game Details Grid -->
        <div class="game-details" :style="detailsContainerStyles">
          <!-- Ratings (if enabled; stored as showDifficulty). The email shows one of these per rating
               the event's customers can see, under each scale's name; this is the default scale's sample. -->
          <div
            v-if="componentData.showDifficulty"
            class="detail-item"
            :style="detailItemStyles"
          >
            <span :style="iconStyles">⭐</span>
            <div class="detail-content">
              <div class="detail-label" :style="labelStyles">
                {{ getLabel('difficulty') }}
              </div>
              <div class="detail-value" :style="valueStyles">
                4/5
              </div>
            </div>
          </div>

          <!-- Duration (if enabled) -->
          <div
            v-if="componentData.showDuration"
            class="detail-item"
            :style="detailItemStyles"
          >
            <span :style="iconStyles">⏱️</span>
            <div class="detail-content">
              <div class="detail-label" :style="labelStyles">
                {{ getLabel('duration') }}
              </div>
              <div class="detail-value" :style="valueStyles">
                60 {{ getLabel('minutes') }}
              </div>
            </div>
          </div>

          <!-- Group Size (if enabled) -->
          <div
            v-if="componentData.showGroupSize"
            class="detail-item"
            :style="detailItemStyles"
          >
            <span :style="iconStyles">👥</span>
            <div class="detail-content">
              <div class="detail-label" :style="labelStyles">
                {{ getLabel('groupSize') }}
              </div>
              <div class="detail-value" :style="valueStyles">
                2-8 {{ getLabel('players') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GameComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      labels: {
        english: {
          difficulty: 'Difficulty',
          duration: 'Duration',
          groupSize: 'Group Size',
          minutes: 'min',
          players: 'players'
        },
        spanish: {
          difficulty: 'Dificultad',
          duration: 'Duración',
          groupSize: 'Tamaño del grupo',
          minutes: 'min',
          players: 'jugadores'
        },
        french: {
          difficulty: 'Difficulté',
          duration: 'Durée',
          groupSize: 'Taille du groupe',
          minutes: 'min',
          players: 'joueurs'
        },
        swedish: {
          difficulty: 'Svårighetsgrad',
          duration: 'Varaktighet',
          groupSize: 'Gruppstorlek',
          minutes: 'min',
          players: 'spelare'
        },
        danish: {
          difficulty: 'Sværhedsgrad',
          duration: 'Varighed',
          groupSize: 'Gruppestørrelse',
          minutes: 'min.',
          players: 'deltagere'
        },
        greek: {
          difficulty: 'Δυσκολία',
          duration: 'Διάρκεια',
          groupSize: 'Μέγεθος ομάδας',
          minutes: 'λεπτά',
          players: 'παίκτες'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    currentLayout() {
      return this.componentData.layout || 'image-top'
    },

    isImageSideLayout() {
      return this.currentLayout === 'image-left' || this.currentLayout === 'image-right'
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    wrapperStyles() {
      if (!this.isImageSideLayout) {
        return { display: 'block' }
      }

      return {
        display: 'flex',
        flexDirection: this.currentLayout === 'image-left' ? 'row' : 'row-reverse',
        gap: '20px',
        alignItems: 'flex-start'
      }
    },

    imageContainerStyles() {
      const styles = this.componentData.styles || {}

      if (this.isImageSideLayout) {
        return {
          flexShrink: '0',
          width: `${styles.imageWidth || '40'}%`,
          minWidth: '150px'
        }
      }

      return {
        marginBottom: '16px',
        textAlign: this.componentData.imageAlignment || 'center'
      }
    },

    imagePlaceholderStyles() {
      const styles = this.componentData.styles || {}

      const baseStyles = {
        backgroundColor: '#f3f4f6',
        borderRadius: `${styles.imageBorderRadius || '8'}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        color: '#9ca3af',
        fontSize: '14px'
      }

      if (this.isImageSideLayout) {
        return {
          ...baseStyles,
          width: '100%',
          height: '250px'
        }
      }

      return {
        ...baseStyles,
        width: `${styles.imageWidth || '100'}%`,
        maxWidth: '100%',
        height: '200px',
        margin: this.componentData.imageAlignment === 'center' ? '0 auto' : '0'
      }
    },

    contentStyles() {
      if (this.isImageSideLayout) {
        return {
          flex: '1',
          minWidth: '0'
        }
      }
      return {}
    },

    nameStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.nameFontSize || '24'}px`,
        fontWeight: styles.nameFontWeight || '700',
        color: styles.nameColor || '#111827',
        margin: '0 0 12px 0',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    descriptionStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.descriptionFontSize || '14'}px`,
        color: styles.descriptionColor || '#6b7280',
        lineHeight: '1.6',
        marginBottom: '16px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    detailsContainerStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'grid',
        gridTemplateColumns: styles.detailsLayout === 'vertical' ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginTop: '16px'
      }
    },

    detailItemStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: `${styles.detailPadding || '8'}px`,
        backgroundColor: styles.detailBackgroundColor || 'transparent',
        borderRadius: `${styles.detailBorderRadius || '4'}px`
      }
    },

    iconStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.iconSize || '18'}px`,
        color: styles.iconColor || '#e14eca',
        flexShrink: '0',
        marginTop: '2px'
      }
    },

    labelStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.labelFontSize || '11'}px`,
        fontWeight: styles.labelFontWeight || '600',
        color: styles.labelColor || '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '2px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    valueStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.valueFontSize || '14'}px`,
        fontWeight: styles.valueFontWeight || '500',
        color: styles.valueColor || '#111827',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    }
  }
}
</script>

<style scoped>
.game-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.game-component:hover,
.game-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.game-wrapper {
  width: 100%;
}

.placeholder-image i {
  font-size: 48px;
}

.game-content {
  width: 100%;
}

.game-name {
  line-height: 1.2;
}

.game-description {
  word-wrap: break-word;
}

.game-details {
  width: 100%;
}

.detail-item {
  min-width: 0;
}

.detail-content {
  min-width: 0;
  flex: 1;
}

.detail-label,
.detail-value {
  word-wrap: break-word;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .game-wrapper {
    flex-direction: column !important;
  }

  .game-image-container {
    width: 100% !important;
  }

  .game-details {
    grid-template-columns: 1fr !important;
  }
}
</style>
